import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Bell, Heart, CheckCheck } from "lucide-react";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationsBell() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications);
      setUnread(data.unread);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    const onNotify = () => load();
    window.addEventListener("blinxd:notify", onNotify);
    return () => { clearInterval(t); window.removeEventListener("blinxd:notify", onNotify); };
  }, [load]);

  const markAll = async () => {
    await api.post("/notifications/read").catch(() => {});
    setUnread(0);
    setItems((xs) => xs.map((x) => ({ ...x, read: true })));
  };

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      await api.post(`/notifications/${n.notification_id}/read`).catch(() => {});
      setUnread((u) => Math.max(0, u - 1));
    }
    if (n.match_id) nav(`/matches/${n.match_id}`);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell"
          className="relative flex items-center justify-center h-9 w-9 rounded-full text-muted-foreground hover:text-ink hover:bg-muted transition-colors">
          <Bell size={18} />
          {unread > 0 && (
            <span data-testid="notifications-unread-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-terracotta text-white text-[10px] font-mono font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-2xl border-stone-200 shadow-card" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200">
          <span className="font-serif font-semibold text-lg">Notifications</span>
          {items.some((x) => !x.read) && (
            <button onClick={markAll} data-testid="notifications-mark-all"
              className="flex items-center gap-1 text-[11px] font-mono text-terracotta hover:underline">
              <CheckCheck size={13} /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">No notifications yet.</div>
          )}
          {items.map((n) => (
            <button key={n.notification_id} onClick={() => openItem(n)} data-testid={`notification-${n.notification_id}`}
              className={`w-full text-left px-4 py-3 flex gap-3 border-b border-stone-100 hover:bg-muted transition-colors ${n.read ? "" : "bg-terracotta-light/20"}`}>
              <div className="h-9 w-9 rounded-full bg-terracotta-light/50 flex items-center justify-center shrink-0">
                <Heart size={16} className="text-terracotta" fill="currentColor" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{n.title}</div>
                <div className="text-xs text-muted-foreground leading-snug mt-0.5">{n.body}</div>
                <div className="text-[10px] font-mono text-muted-foreground mt-1">{timeAgo(n.created_at)}</div>
              </div>
              {!n.read && <span className="h-2 w-2 rounded-full bg-terracotta mt-1.5 shrink-0" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
